import { DashboardNavbar } from "@/components/dashboard/DashboardNavbar";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MessageSquare, TrendingUp, Users, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { SendCampaignDialog } from "@/components/campaigns/SendCampaignDialog";
import { ProGate } from "@/components/ProGate";

interface Campaign {
  campaign_id: string;
  message: string;
  recipient_count: number;
  sent_count: number;
  created_at: string;
  attributed_bookings_count: number;
  attributed_revenue: number;
}

export default function CampaignsPage() {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [businessId, setBusinessId] = useState<string | null>(null);

  // Stats calculations
  const totalSent = campaigns.reduce((sum, c) => sum + c.sent_count, 0);
  const totalBookings = campaigns.reduce((sum, c) => sum + c.attributed_bookings_count, 0);
  const totalRevenue = campaigns.reduce((sum, c) => sum + c.attributed_revenue, 0);

  const loadBusinessProfile = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('business_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      setBusinessId(data.id);
    } catch (error) {
      console.error('Error loading business profile:', error);
      toast.error('Failed to load business profile');
    }
  };

  const loadCampaigns = async () => {
    if (!businessId) {
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.rpc('get_recent_campaigns', {
        p_business_id: businessId,
        p_limit: 10
      });

      if (error) throw error;
      setCampaigns(data || []);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Win Back Clients | Figgy.link";
  }, []);

  useEffect(() => {
    if (user) {
      loadBusinessProfile();
    }
  }, [user]);

  useEffect(() => {
    if (businessId) {
      loadCampaigns();
    }
  }, [businessId]);

  const handleCampaignSent = () => {
    setSendDialogOpen(false);
    loadCampaigns();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardNavbar />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-semibold">Win Back Clients</h1>
            <p className="text-muted-foreground">
              Re-engage inactive clients with SMS campaigns
            </p>
          </div>
          <ProGate
            featureName="Win Back Campaigns"
            featureDescription="Send personalized SMS campaigns to re-engage inactive clients. Available on the Pro plan."
          >

          {/* Recent Results Stats */}
          {campaigns.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <MessageSquare className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Messages Sent</p>
                    <p className="text-2xl font-semibold">{totalSent}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Users className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Bookings</p>
                    <p className="text-2xl font-semibold">{totalBookings}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Revenue</p>
                    <p className="text-2xl font-semibold">
                      ${totalRevenue.toFixed(2)}
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Campaign Action Card */}
          <Card className="p-6 mb-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-2">
                  Re-engage Inactive Clients
                </h3>
                <p className="text-muted-foreground mb-4">
                  Send a personalized SMS to up to 25 clients whose last past booking was 21+ days ago
                  and don't have upcoming appointments. Your booking link is automatically included.
                  Clients can only be contacted once per month.
                </p>
                <Button
                  className="bg-figgy-500 hover:bg-figgy-600"
                  onClick={() => setSendDialogOpen(true)}
                  disabled={!businessId}
                >
                  <Send className="mr-2 h-4 w-4" />
                  Send Campaign
                </Button>
              </div>
            </div>
          </Card>

          {/* Recent Campaigns */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Recent Campaigns</h2>
            </div>

            {isLoading ? (
              <div className="p-4 text-center text-muted-foreground">
                Loading campaigns...
              </div>
            ) : campaigns.length > 0 ? (
              <div className="divide-y">
                {campaigns.map((campaign) => (
                  <div key={campaign.campaign_id} className="p-6 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-sm font-medium text-gray-900">
                            {new Date(campaign.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            Sent {campaign.sent_count} of {campaign.recipient_count} clients
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                          {campaign.message}
                        </p>
                        <div className="flex gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Bookings: </span>
                            <span className="font-medium">{campaign.attributed_bookings_count}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Revenue: </span>
                            <span className="font-medium">${campaign.attributed_revenue.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                  <MessageSquare className="h-6 w-6 text-gray-600" />
                </div>
                <h3 className="mt-2 text-sm font-semibold text-gray-900">No campaigns yet</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Send your first campaign to re-engage inactive clients
                </p>
                <div className="mt-6">
                  <Button
                    className="bg-figgy-500 hover:bg-figgy-600"
                    onClick={() => setSendDialogOpen(true)}
                    disabled={!businessId}
                  >
                    <Send className="mr-2 h-4 w-4" />
                    Send Campaign
                  </Button>
                </div>
              </div>
            )}
          </div>
          </ProGate>
        </div>
      </div>

      {businessId && (
        <SendCampaignDialog
          open={sendDialogOpen}
          onOpenChange={setSendDialogOpen}
          businessId={businessId}
          onCampaignSent={handleCampaignSent}
        />
      )}
    </div>
  );
}
